import Link from 'next/link';
import classNames from 'classnames';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import Carousel from '@/components/Carousel/Carousel';
import { DescriptionType, ImageType } from '@/utils/common.types';

interface Props {
  backgroundColor: string;
  description: DescriptionType;
  includeCardShadow: boolean;
  photoGalleries: {
    __typename?: string;
    title: string;
    description?: DescriptionType;
    imagesCollection: ImageType[];
    isDigitalModel?: boolean;
    modelTitle?: string;
    numberBeds?: number;
    numberBaths?: number;
    modelLength?: number;
    modelWidth?: number;
    modelSlug?: string;
  }[];
  title: string;
}

const PhotoGalleryGrid = ({
  title,
  description,
  photoGalleries = [],
  includeCardShadow,
  backgroundColor,
}: Props) => {
  const generateModelDetails = (gallery) => (
    <div>
      <div>
        {gallery?.modelTitle && (
          <h4 className="font-bold">{gallery.modelTitle}</h4>
        )}
        <div className="flex flex-row text-black-standard">
          {gallery?.numberBeds && (
            <p className="mr-6">{gallery.numberBeds} beds</p>
          )}
          {gallery?.numberBaths && (
            <p className="mr-6 list-item list-disc">
              {gallery.numberBaths} baths
            </p>
          )}
          {gallery?.modelLength && gallery?.modelWidth && (
            <p className="mr-6 list-item list-disc">
              {gallery.modelLength * gallery.modelWidth} sq ft
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section
      className={classNames(
        'mx-auto my-[30px] w-mobile max-w-standard text-hbg-blue',
        {
          ['p-5 shadow-component']: includeCardShadow,
        },
        {
          ['bg-grey-4']: backgroundColor === 'grey4',
          ['bg-grey-5']: backgroundColor === 'grey5',
          ['bg-grey-6']: backgroundColor === 'grey6',
        }
      )}
    >
      {title && (
        <div className="mb-[30px] font-acumin text-[22px] font-bold md:text-[26px] lg:text-[35px]">
          <h3>{title}</h3>
        </div>
      )}
      {description && (
        <div className="mb-[30px] text-[1rem] lg:text-[1.15rem]">
          {ContentfulRichText(description)}
        </div>
      )}
      {photoGalleries?.length > 0 && (
        <div
          className="grid w-full lg:!grid-cols-3 [@media(min-width:600px)]:grid-cols-2 [@media(min-width:600px)]:gap-x-6"
          id="gallery"
        >
          {photoGalleries.map(
            (gallery, key) =>
              gallery?.imagesCollection && (
                <div key={key} className="flex flex-col">
                  <Carousel
                    gallery={gallery.imagesCollection}
                    isDigitalModel={gallery?.isDigitalModel}
                    modelSlug={gallery?.modelSlug || null}
                    includeThumbnails={false}
                    isGalleryGrid={true}
                  />
                  {gallery?.isDigitalModel &&
                    (gallery?.modelSlug ? (
                      <Link
                        href={gallery.modelSlug}
                        target="_self"
                        className="no-underline"
                      >
                        {generateModelDetails(gallery)}
                      </Link>
                    ) : (
                      generateModelDetails(gallery)
                    ))}
                </div>
              )
          )}
        </div>
      )}
    </section>
  );
};

export default PhotoGalleryGrid;
